import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreditCard, Sparkles, Check, Loader2, Crown, AlertCircle, Globe2, MapPin, AlertTriangle, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/App";
import CancelSurveyModal from "@/components/CancelSurveyModal";

/**
 * Billing & Subscription card — shown near the top of SettingsPage.
 *
 * Lifecycle
 * ---------
 * 1. On mount → GET /api/billing/status to determine tier/expiry.
 * 2. If URL has ?status=success&session_id=xxx → poll
 *    /api/payments/v1/checkout/status/{session_id} every 2s (max 5 tries);
 *    on `payment_status === 'paid'` we refresh /api/billing/status and
 *    surface a success banner + clear the URL params.
 * 3. Free user clicks "Upgrade" → POST /api/billing/checkout → redirect.
 * 4. Pro user clicks "Renew" → same flow with pro_monthly package.
 *
 * The price is UNIVERSALLY $6/mo USD regardless of the user's kitchen
 * currency (Stripe billing ≠ kitchen operational currency).
 */
export default function BillingSettingsCard() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pollMsg, setPollMsg] = useState(null);
  const [successFlash, setSuccessFlash] = useState(false);
  const pollTimerRef = useRef(null);

  // Billing region intercept (Migration 010).  Stored in localStorage so a
  // returning chef doesn't see the picker every time.  Server-side persistence
  // happens after the first successful Stripe payment via the customers table.
  const [billingRegion, setBillingRegion] = useState(() => {
    try {
      return window.localStorage.getItem("pp_billing_region") || null;
    } catch {
      return null;
    }
  });
  const [showRegionIntercept, setShowRegionIntercept] = useState(false);
  const [pendingTier, setPendingTier] = useState("professional");
  const [showCancelSurvey, setShowCancelSurvey] = useState(false);

  // Failed-payment retry banner state — supports BOTH Razorpay (IN) and
  // Stripe (RoW).  Normalized to a single shape: {key, label, gateway,
  // amount_display, reason, retry_count, raw}.
  const [lastFailure, setLastFailure] = useState(null);
  const [failureDismissed, setFailureDismissed] = useState(() => {
    try {
      return window.localStorage.getItem("pp_failure_dismissed") || null;
    } catch {
      return null;
    }
  });

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/billing/status`);
      setStatus(res.data);
    } catch {
      setStatus({ subscription_tier: "individual", is_pro: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchLastFailure = async () => {
    // Razorpay is the only live gateway; xPay banner will land in the next
    // sprint once credentials are provisioned.
    try {
      const rzpRes = await axios.get(`${API}/billing/razorpay/last-failure`);
      const rzp = rzpRes.data?.last_failure || null;
      if (!rzp) {
        setLastFailure(null);
        return;
      }
      setLastFailure({
        key: rzp.order_id,
        gateway: "razorpay",
        failed_at: rzp.failed_at,
        amount_display: `₹${Number(rzp.amount_inr).toFixed(0)}`,
        reason: rzp.reason,
        retry_count: rzp.retry_count,
        raw: rzp,
      });
    } catch {
      setLastFailure(null);
    }
  };

  const dismissFailureBanner = () => {
    if (!lastFailure?.key) return;
    try {
      window.localStorage.setItem("pp_failure_dismissed", lastFailure.key);
    } catch {
      /* ignore */
    }
    setFailureDismissed(lastFailure.key);
  };

  const retryFailedPayment = () => {
    if (!lastFailure) return;
    if (lastFailure.gateway === "razorpay") {
      handleRazorpayUpgrade({ retryOf: lastFailure.key });
    } else {
      handleUpgrade({ retryOf: lastFailure.key });
    }
  };

  // Initial load
  useEffect(() => {
    refresh();
    fetchLastFailure();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Post-Stripe return poller removed with Stripe migration to xPay.
  // xPay's afterCompletion redirect will land here in a follow-up sprint.
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam === "cancelled") {
      toast.info(
        t("billing.checkoutCancelled", {
          defaultValue: "Upgrade cancelled. Nothing was charged.",
        })
      );
      setSearchParams({ tab: "billing" });
    }
  }, [searchParams, setSearchParams, t]);

  const handleUpgrade = async (opts = {}) => {
    const tier = opts.tier || "professional";
    // Region intercept — first-time upgraders must explicitly choose IN vs RoW.
    if (!billingRegion) {
      setPendingTier(tier);
      setShowRegionIntercept(true);
      return;
    }
    if (billingRegion === "IN") {
      await handleRazorpayUpgrade({ retryOf: opts.retryOf, tier });
      return;
    }
    // RoW / xPay path — credentials pending.  Show a friendly notice so
    // underwriters can inspect the pricing surface without a broken CTA.
    toast.info(
      t("billing.xpayComingSoon", {
        defaultValue:
          "USD checkout via xPay is launching shortly. We'll email you the moment it goes live.",
      }),
      { duration: 6000 }
    );
  };

  // ---- Razorpay (India) flow ---------------------------------------------
  const ensureRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const existing = document.getElementById("razorpay-checkout-js");
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("script load failed")), { once: true });
        return;
      }
      const script = document.createElement("script");
      script.id = "razorpay-checkout-js";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Could not load Razorpay Checkout.js"));
      document.body.appendChild(script);
    });

  const handleRazorpayUpgrade = async (opts = {}) => {
    const tier = opts.tier || "professional";
    const packageId = tier === "artisan" ? "artisan_monthly" : "pro_monthly";
    try {
      setCheckingOut(true);
      await ensureRazorpayScript();
      const orderRes = await axios.post(`${API}/billing/razorpay/checkout`, {
        package_id: packageId,
        retry_of: opts.retryOf || null,
      });
      const order = orderRes.data;

      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: order.name,
        description: order.description,
        prefill: {
          email: order.prefill_email || undefined,
          name: order.prefill_name || undefined,
        },
        theme: { color: "#f59e0b" },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/billing/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data?.success) {
              toast.success(
                t("billing.razorpaySuccess", {
                  defaultValue: "Payment verified — you're now PlateProfit Pro!",
                })
              );
              setSuccessFlash(true);
              setTimeout(() => setSuccessFlash(false), 6000);
              await refresh();
              await fetchLastFailure();
            } else {
              toast.error(t("billing.razorpayVerifyFailed", { defaultValue: "Verification failed" }));
            }
          } catch (err) {
            toast.error(
              err?.response?.data?.detail ||
                t("billing.razorpayVerifyFailed", { defaultValue: "Verification failed" })
            );
          }
        },
        modal: {
          ondismiss: () => {
            toast.info(t("billing.razorpayDismissed", { defaultValue: "Checkout cancelled" }));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(
          resp?.error?.description ||
            t("billing.razorpayFailed", { defaultValue: "Payment failed" })
        );
      });
      rzp.open();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          t("billing.razorpayFailed", { defaultValue: "Couldn't start Razorpay checkout" })
      );
    } finally {
      setCheckingOut(false);
    }
  };

  const selectRegion = (region) => {
    try {
      window.localStorage.setItem("pp_billing_region", region);
    } catch {
      /* ignore */
    }
    setBillingRegion(region);
    setShowRegionIntercept(false);
    // Auto-continue with the just-picked region + the tier the user asked for.
    const tier = pendingTier || "professional";
    setTimeout(() => {
      if (region === "RoW") {
        handleUpgrade({ tier });
      } else {
        handleRazorpayUpgrade({ tier });
      }
    }, 50);
  };

  if (loading) {
    return (
      <div
        id="billing"
        className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 flex items-center gap-3 text-slate-400"
        data-testid="billing-card-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  const isPro = status?.is_pro === true;
  const expiresAt = status?.pro_expires_at
    ? new Date(status.pro_expires_at)
    : null;

  return (
    <div
      id="billing"
      className={
        "rounded-xl border p-6 space-y-4 " +
        (isPro
          ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900"
          : "border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-slate-900 to-slate-900")
      }
      data-testid="billing-settings-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={
              "flex-shrink-0 h-11 w-11 rounded-full flex items-center justify-center border " +
              (isPro
                ? "bg-emerald-500/15 border-emerald-500/40"
                : "bg-amber-500/15 border-amber-500/40")
            }
          >
            {isPro ? (
              <Crown className="h-5 w-5 text-emerald-400" />
            ) : (
              <CreditCard className="h-5 w-5 text-amber-400" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span
                className={
                  "text-[0.6rem] uppercase tracking-[0.25em] font-semibold " +
                  (isPro ? "text-emerald-400" : "text-amber-400")
                }
              >
                {t("billing.sectionLabel", { defaultValue: "Billing & Subscription" })}
              </span>
            </div>
            <h2 className="text-xl font-bold text-white">
              {isPro
                ? t("billing.proActive", { defaultValue: "PlateProfit Pro — Active" })
                : t("billing.freePlan", { defaultValue: "Free Plan" })}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {isPro
                ? expiresAt
                  ? t("billing.renewsOn", {
                      defaultValue: "Active until {{date}}",
                      date: expiresAt.toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }),
                    })
                  : t("billing.proActiveBody", {
                      defaultValue: "Tourist Menu + AI translations unlocked.",
                    })
                : t("billing.freeBody", {
                    defaultValue:
                      "Core features are free forever. Upgrade to unlock AI Tourist Menu translations.",
                  })}
            </p>
          </div>
        </div>
      </div>

      {successFlash && (
        <div
          className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300"
          data-testid="billing-success-flash"
        >
          <Check className="h-4 w-4" />
          {t("billing.welcomePro", {
            defaultValue: "Welcome to Pro — all premium features are unlocked.",
          })}
        </div>
      )}

      {pollMsg && (
        <div
          className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300"
          data-testid="billing-verify-banner"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {pollMsg}
        </div>
      )}

      {!isPro && lastFailure && failureDismissed !== lastFailure.key && (
        <div
          className="rounded-lg border border-amber-500/50 bg-gradient-to-br from-amber-500/10 to-rose-500/5 p-4 space-y-3"
          data-testid="payment-failure-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-none mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-amber-200">
                {t("billing.retry.title", { defaultValue: "Last payment didn't go through" })}
              </div>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                <span className="font-semibold text-white">
                  {lastFailure.amount_display}
                </span>{" "}
                {t("billing.retry.attemptOn", { defaultValue: "attempt on" })}{" "}
                <span className="font-mono text-amber-300">
                  {new Date(lastFailure.failed_at).toLocaleDateString(undefined, {
                    year: "numeric", month: "short", day: "numeric",
                  })}
                </span>
                {lastFailure.reason ? ` · ${lastFailure.reason}` : null}
                {lastFailure.retry_count > 1 ? (
                  <span className="ml-1 text-slate-500">
                    · {t("billing.retry.attemptN", {
                      defaultValue: "attempt #{{n}}",
                      n: lastFailure.retry_count,
                    })}
                  </span>
                ) : null}
              </p>
            </div>
            <button
              type="button"
              onClick={dismissFailureBanner}
              className="flex-none text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={t("common.dismiss", { defaultValue: "Dismiss" })}
              data-testid="payment-failure-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Button
            onClick={retryFailedPayment}
            disabled={checkingOut}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            data-testid="payment-failure-retry-btn"
          >
            {checkingOut ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {t("billing.retry.cta", { defaultValue: "Try again" })}
          </Button>
        </div>
      )}

      {!isPro && (
        <div className="space-y-3 pt-2 border-t border-slate-800">
          <ul className="text-sm text-slate-300 space-y-1.5">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              {t("billing.benefit1", {
                defaultValue: "Tourist Menu — auto-translate recipes into 7 languages",
              })}
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              {t("billing.benefit2", {
                defaultValue: "500 AI translations / month · cached forever",
              })}
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              {t("billing.benefit3", {
                defaultValue: "Priority support + early access to new features",
              })}
            </li>
          </ul>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            {/* Artisan tier CTA */}
            <button
              type="button"
              onClick={() => handleUpgrade({ tier: "artisan" })}
              disabled={checkingOut}
              className="group relative rounded-xl border border-slate-700 bg-slate-800/60 hover:border-emerald-500/60 hover:bg-slate-800 transition-colors p-4 text-left disabled:opacity-60"
              data-testid="billing-upgrade-artisan"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">
                  {t("billing.artisan.label", { defaultValue: "Artisan" })}
                </span>
                <span className="text-lg font-bold text-white">
                  {billingRegion === "IN" ? "₹249" : "$2.99"}
                  <span className="text-xs font-normal text-slate-400">/mo</span>
                </span>
              </div>
              <p className="text-xs text-slate-400 leading-snug">
                {t("billing.artisan.desc", {
                  defaultValue: "Master Recipe Bank — 250 recipes, 6-decimal costing.",
                })}
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[0.65rem] text-slate-500">
                  {t("billing.artisan.note", { defaultValue: "Ideal for solo chefs" })}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400 group-hover:translate-x-0.5 transition-transform">
                  {checkingOut ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {t("billing.chooseCta", { defaultValue: "Choose" })}
                </span>
              </div>
            </button>

            {/* Professional tier CTA (primary / recommended) */}
            <button
              type="button"
              onClick={() => handleUpgrade({ tier: "professional" })}
              disabled={checkingOut}
              className="group relative rounded-xl border-2 border-amber-500 bg-gradient-to-br from-amber-500/15 to-orange-500/10 hover:from-amber-500/25 transition-colors p-4 text-left disabled:opacity-60"
              data-testid="billing-upgrade-button"
            >
              <div className="absolute -top-2.5 right-3 bg-amber-500 text-slate-950 text-[0.6rem] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                {t("billing.mostPopular", { defaultValue: "Most Popular" })}
              </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">
                  {t("billing.professional.label", { defaultValue: "Professional" })}
                </span>
                <span className="text-lg font-bold text-white">
                  {billingRegion === "IN" ? "₹499" : "$5.99"}
                  <span className="text-xs font-normal text-slate-400">/mo</span>
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-snug">
                {t("billing.professional.desc", {
                  defaultValue:
                    "Unlimited recipes + Tourist Menu AI translations + priority support.",
                })}
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[0.65rem] text-amber-300/80">
                  {t("billing.professional.note", { defaultValue: "Everything in Artisan +" })}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-300 group-hover:translate-x-0.5 transition-transform">
                  {checkingOut ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {t("billing.chooseCta", { defaultValue: "Choose" })}
                </span>
              </div>
            </button>
          </div>
          <p className="text-[0.7rem] text-slate-500 italic">
            <AlertCircle className="inline h-3 w-3 mr-1" />
            {t("proPaywall.stripeBillingNote")}
          </p>
        </div>
      )}

      {isPro && (
        <div className="pt-2 border-t border-emerald-500/20">
          <Button
            type="button"
            onClick={handleUpgrade}
            disabled={checkingOut}
            variant="outline"
            className="w-full bg-slate-800 border-slate-700 text-white hover:bg-slate-700 gap-2"
            data-testid="billing-renew-button"
          >
            {checkingOut ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {t("billing.renewPro", { defaultValue: "Renew Pro ({{price}})" , price: t("proPaywall.price") })}
          </Button>
          <p className="text-[0.7rem] text-slate-500 italic mt-2 text-center">
            {t("billing.renewHint", {
              defaultValue:
                "Each renewal stacks 30 days on top of your current expiry — no time is lost.",
            })}
          </p>
          <button
            type="button"
            onClick={() => setShowCancelSurvey(true)}
            className="w-full mt-3 text-xs text-slate-500 hover:text-rose-300 underline-offset-4 hover:underline py-2"
            data-testid="billing-unsubscribe-link"
          >
            {t("billing.unsubscribe", { defaultValue: "Unsubscribe" })}
          </button>
        </div>
      )}

      <CancelSurveyModal
        open={showCancelSurvey}
        onClose={() => setShowCancelSurvey(false)}
        onCancelled={async () => { try { await refresh(); } catch { /* ignore */ } }}
      />

      {/* ============== BILLING REGION INTERCEPT ============== */}
      <Dialog open={showRegionIntercept} onOpenChange={setShowRegionIntercept}>
        <DialogContent
          className="max-w-md bg-slate-900 border-amber-500/40"
          data-testid="billing-region-intercept-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2 text-white">
              <MapPin className="h-5 w-5 text-amber-400" />
              {t("billing.regionInterceptTitle", { defaultValue: "Where do you bill from?" })}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {t("billing.regionInterceptBody", {
                defaultValue:
                  "We route payments to the right processor based on your country. This choice is saved for future renewals.",
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <button
              type="button"
              onClick={() => selectRegion("IN")}
              className="rounded-lg border border-slate-700 bg-slate-800/60 p-4 hover:border-amber-500/60 hover:bg-slate-800 transition-colors text-left group"
              data-testid="billing-region-india"
            >
              <div className="text-3xl mb-2">🇮🇳</div>
              <div className="text-sm font-bold text-white mb-0.5">
                {t("billing.regionIndia", { defaultValue: "India" })}
              </div>
              <div className="text-[0.7rem] text-slate-400 leading-snug">
                {t("billing.regionIndiaSub", {
                  defaultValue: "INR · UPI · Cards · Razorpay",
                })}
              </div>
            </button>
            <button
              type="button"
              onClick={() => selectRegion("RoW")}
              className="rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-500/15 to-slate-900 p-4 hover:border-amber-400 transition-colors text-left"
              data-testid="billing-region-row"
            >
              <div className="text-3xl mb-2">🌍</div>
              <div className="text-sm font-bold text-white mb-0.5">
                {t("billing.regionRoW", { defaultValue: "Rest of World" })}
              </div>
              <div className="text-[0.7rem] text-slate-400 leading-snug">
                {t("billing.regionRoWSub", {
                  defaultValue: "USD · xPay (Merchant of Record)",
                })}
              </div>
            </button>
          </div>
          <p className="text-[0.65rem] text-slate-500 italic text-center">
            {t("billing.regionPriceNote", {
              defaultValue:
                "Artisan $2.99/mo · Pro $5.99/mo (xPay MoR, RoW) · Artisan ₹249/mo · Pro ₹499/mo (Razorpay, IN)",
            })}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
